import { StateCreator } from 'zustand';
import {
	createPaginatorSlice,
	createTableManagerStoreSliceFactory,
	PaginatorStore,
	TableManagerStore,
} from '@warehouse/shared/ui';
import { definePersistentQueryParamStore } from '@warehouse/shared/util';
import { NxObjectType } from '@warehouse/object-browser/core';
import { nxObjectRepositorySingleton } from '@warehouse/object-browser/infra';
import {
	brokerJobsRepositorySingleton,
	brokerServicesRepositorySingleton,
} from '@warehouse/object-browser/feature-broker';
import { BrokerJob, BrokerService, TenantJobSearchFilters } from '@warehouse/object-browser/feature-broker-core';
import {
	CopiesByETag,
	intersectPrefixes,
	JobReference,
	JobReferenceParser,
	ObjectsByUuid,
	searchTextToTenantJobSearchFilters,
	ServiceLogColumnField,
	ServiceLogFilterAdapter,
	ServiceLogStatusQuickFilter,
} from '@warehouse/service-log/core';

/** How often the page re-reads the Broker while at least one listed job is still open. */
export const SERVICE_LOG_AUTO_REFRESH_MS = 30_000;

type ServiceLogTableManager = TableManagerStore<ServiceLogColumnField>;

export interface ServiceLogStore extends Omit<ServiceLogTableManager, 'actions'>, Omit<PaginatorStore, 'actions'> {
	jobs: BrokerJob[];
	totalJobs: number;
	/** What each listed job ran on, parsed once from its id when the page loads (see JobReferenceParser). */
	referencesByJobId: Record<string, JobReference>;
	/**
	 * Inventory objects behind uuid-keyed jobs, by uuid (`null` = not in the Inventory). Re-read on every load, so a
	 * refresh shows what the Inventory holds now; earlier values stay on screen until the new ones arrive.
	 */
	objectsByUuid: ObjectsByUuid;
	/** Inventory copies by eTag: of eTag-keyed jobs' content, and of the files uuid-keyed jobs ran on. Re-read on every load. */
	copiesByETag: CopiesByETag;
	isLoading: boolean;
	isRefreshing: boolean;
	isFetchError: boolean;
	statusQuickFilter: ServiceLogStatusQuickFilter;
	searchText: string;
	actions: ServiceLogTableManager['actions'] &
		PaginatorStore['actions'] & {
			/** (Re)loads the current page from the Broker with the current filters. */
			load(): Promise<void>;
			refresh(): Promise<void>;
			setStatusQuickFilter(statusQuickFilter: ServiceLogStatusQuickFilter): void;
			setSearchText(searchText: string): void;
		};
}

export const createServiceLogStore: StateCreator<ServiceLogStore> = (set, get, store) => {
	const tableManager = createTableManagerStoreSliceFactory<ServiceLogColumnField>({ getPaginatorStore: get })(
		set,
		get,
		store,
	);
	const paginator = createPaginatorSlice(set, get, store);

	/**
	 * The ETag and Bucket columns: each job id names one object (uuid) or one piece of content (eTag), see
	 * JobReferenceParser. The objects are looked up, then the copies of the files among them; the copies of the
	 * eTag-keyed content are looked up alongside. Never skipped: objects get deleted and copies made while the page is
	 * open. Best-effort and independent: a failed lookup leaves its own cells unresolved, the other lookups and the
	 * jobs themselves still show.
	 */
	const resolveReferences = async (references: JobReference[], sequence: number) => {
		const uuids = new Set<string>();
		const eTags = new Set<string>();
		references.forEach((reference) => {
			if (reference.kind === 'object') uuids.add(reference.nxObjectUuid);
			if (reference.kind === 'content') eTags.add(reference.eTag);
		});
		await Promise.allSettled([resolveObjects([...uuids], eTags, sequence), resolveCopies([...eTags], sequence)]);
	};

	/** The objects behind uuid-keyed jobs, then the copies of the files among them not already looked up. */
	const resolveObjects = async (uuids: string[], contentETags: Set<string>, sequence: number) => {
		if (uuids.length === 0) return;
		const objects = await nxObjectRepositorySingleton.get().findNxObjectsByUuids({ uuids });
		if (sequence !== loadSequence) return;
		set((state) => ({
			objectsByUuid: {
				...state.objectsByUuid,
				...Object.fromEntries(uuids.map((uuid) => [uuid, null])),
				...Object.fromEntries(objects.map((object) => [object.uuid, object])),
			},
		}));
		const fileETags = new Set(
			objects.flatMap((object) => (object.itemType === NxObjectType.File ? [object.eTag] : [])),
		);
		await resolveCopies(
			[...fileETags].filter((eTag) => !contentETags.has(eTag)),
			sequence,
		);
	};

	const resolveCopies = async (eTags: string[], sequence: number) => {
		if (eTags.length === 0) return;
		const copies = await nxObjectRepositorySingleton.get().findNxObjectsByETags({ eTags });
		if (sequence !== loadSequence) return;
		set((state) => ({
			copiesByETag: {
				...state.copiesByETag,
				...Object.fromEntries(
					eTags.map((eTag) => [
						eTag,
						copies.filter((copy) => copy.itemType === NxObjectType.File && copy.eTag === eTag),
					]),
				),
			},
		}));
	};

	let loadSequence = 0;
	const load = async ({ refreshing }: { refreshing: boolean }) => {
		loadSequence += 1;
		const sequence = loadSequence;
		set(refreshing ? { isRefreshing: true } : { isLoading: true });
		try {
			const { page, perPage } = get();
			const filters = serviceLogSearchFilters(
				get(),
				brokerServicesRepositorySingleton.get().getAvailableServicesConfig(),
			);
			const result = filters
				? await brokerJobsRepositorySingleton.get().searchTenantJobs({ page, perPage, filters })
				: { jobs: [], totalJobs: 0, page, perPage };
			if (sequence !== loadSequence) return; // a newer load superseded this one
			const totalPages = Math.max(1, Math.ceil(result.totalJobs / perPage));
			paginator.actions.setTotalCount(result.totalJobs);
			paginator.actions.setTotalPages(totalPages);
			if (page > totalPages) {
				// The result set shrank under us (jobs left the Running chip on a refresh, a cancel): this page no longer
				// exists. Move to the last one that does and keep the current rows; the page change triggers the reload
				// (ServiceLogStoreEffects), so the Broker is asked once, not twice.
				paginator.actions.setPage(totalPages);
				set({ totalJobs: result.totalJobs, isFetchError: false });
				return;
			}
			const servicesByUuid = new Map(
				brokerServicesRepositorySingleton
					.get()
					.getAvailableServicesConfig()
					.map((service) => [service.serviceUUID, service]),
			);
			const referencesByJobId = Object.fromEntries(
				result.jobs.map((job) => [job.jobId, JobReferenceParser.parse(job, servicesByUuid.get(job.serviceUUID))]),
			);
			set({
				// Broker order, newest first, exactly like the Logs tab.
				jobs: result.jobs,
				referencesByJobId,
				totalJobs: result.totalJobs,
				isFetchError: false,
			});
			set({ isLoading: false, isRefreshing: false });
			await resolveReferences(Object.values(referencesByJobId), sequence);
		} catch (error) {
			if (sequence !== loadSequence) return;
			// The rows and the count belonged to the previous query: keeping them under the new filters would pass them
			// off as its result.
			paginator.actions.setTotalCount(0);
			paginator.actions.setTotalPages(1);
			set({ jobs: [], referencesByJobId: {}, totalJobs: 0, isFetchError: true });
		} finally {
			if (sequence === loadSequence) set({ isLoading: false, isRefreshing: false });
		}
	};

	return {
		...tableManager,
		...paginator,
		perPage: 25,
		jobs: [],
		totalJobs: 0,
		referencesByJobId: {},
		objectsByUuid: {},
		copiesByETag: {},
		isLoading: true,
		isRefreshing: false,
		isFetchError: false,
		statusQuickFilter: 'ALL',
		searchText: '',
		actions: {
			...tableManager.actions,
			...paginator.actions,
			load: () => load({ refreshing: false }),
			refresh: () => load({ refreshing: true }),
			setStatusQuickFilter(statusQuickFilter) {
				set({ statusQuickFilter });
				paginator.internalActions.resetPagination();
			},
			setSearchText(searchText) {
				if (searchText === get().searchText) return;
				set({ searchText });
				paginator.internalActions.resetPagination();
			},
		},
	};
};

type ServiceLogSearchInputs = Pick<ServiceLogStore, 'filter' | 'searchText' | 'statusQuickFilter'>;

/**
 * The Broker search filters for the page: column filters, the status chip and the search text, ANDed, which for
 * list-valued conditions means intersecting (the Broker ORs the values inside one condition). `null` when they
 * contradict each other: nothing can match, no call needed.
 */
export function serviceLogSearchFilters(
	state: ServiceLogSearchInputs,
	services: BrokerService[],
): TenantJobSearchFilters | null {
	const fromColumns = ServiceLogFilterAdapter.toTenantJobSearchFilters(state.filter, services);
	const fromSearch = searchTextToTenantJobSearchFilters(state.searchText, services);
	const fromChip: TenantJobSearchFilters =
		state.statusQuickFilter === 'ALL' ? {} : { statuses: [state.statusQuickFilter] };

	const excluded = fromColumns.excludedServiceUUIDs ?? [];
	const serviceUUIDs = intersect(fromColumns.serviceUUIDs, fromSearch.serviceUUIDs)?.filter(
		(uuid) => !excluded.includes(uuid),
	);
	const statuses = intersect(intersect(fromColumns.statuses, fromSearch.statuses), fromChip.statuses);
	// The column's "starts with" and the search box's caller prefix are ANDed too (not concatenated, which the
	// Broker would OR): a name must start with both, so only the longer of two nested prefixes survives.
	const startsWith = intersectPrefixes(
		fromColumns.triggeredByDisplayName?.startsWith,
		fromSearch.triggeredByDisplayName?.startsWith,
	);
	const equals = fromColumns.triggeredByDisplayName?.equals;
	if (serviceUUIDs?.length === 0 || statuses?.length === 0 || startsWith?.length === 0 || equals?.length === 0) {
		return null;
	}

	return {
		...(serviceUUIDs && { serviceUUIDs }),
		// A positive service list already leaves the excluded services out.
		...(!serviceUUIDs && excluded.length > 0 && { excludedServiceUUIDs: excluded }),
		...(statuses && { statuses }),
		...((equals || startsWith) && {
			triggeredByDisplayName: { ...(equals && { equals }), ...(startsWith && { startsWith }) },
		}),
	};
}

/** undefined = no constraint; [] = contradiction. */
function intersect<T>(a: T[] | undefined, b: T[] | undefined): T[] | undefined {
	if (!a) return b;
	if (!b) return a;
	return a.filter((value) => b.includes(value));
}

export const [ServiceLogStoreProvider, useServiceLogStore, ServiceLogStoreContext] =
	definePersistentQueryParamStore<ServiceLogStore>(createServiceLogStore);

export const serviceLogStoreSelector = {
	jobs: (state: ServiceLogStore) => state.jobs,
	totalJobs: (state: ServiceLogStore) => state.totalJobs,
	/** Per-cell selectors: a cell re-renders only when its own job's entries change, not on every lookup. */
	referenceOf: (jobId: string) => (state: ServiceLogStore) => state.referencesByJobId[jobId],
	objectOf: (uuid: string | undefined) => (state: ServiceLogStore) =>
		uuid === undefined ? undefined : state.objectsByUuid[uuid],
	copiesOf: (eTag: string | undefined) => (state: ServiceLogStore) =>
		eTag === undefined ? undefined : state.copiesByETag[eTag],
	isLoading: (state: ServiceLogStore) => state.isLoading,
	isRefreshing: (state: ServiceLogStore) => state.isRefreshing,
	isFetchError: (state: ServiceLogStore) => state.isFetchError,
	statusQuickFilter: (state: ServiceLogStore) => state.statusQuickFilter,
	searchText: (state: ServiceLogStore) => state.searchText,
	hasOpenJobs: (state: ServiceLogStore) => state.jobs.some((job) => job.closedAt === null),
	/**
	 * The filters actually sent to the Broker, serialised: stable across filter-panel edits that change nothing
	 * the Broker sees (an empty row, an operator switch with no value, a column it cannot filter on).
	 */
	searchFiltersKey: (state: ServiceLogStore) =>
		JSON.stringify(
			serviceLogSearchFilters(state, brokerServicesRepositorySingleton.get().getAvailableServicesConfig()),
		),
	// Table manager (consumed by ServerDataGrid)
	sort: (state: ServiceLogStore) => state.sort,
	filter: (state: ServiceLogStore) => state.filter,
	visibility: (state: ServiceLogStore) => state.visibility,
	pinned: (state: ServiceLogStore) => state.pinned,
	order: (state: ServiceLogStore) => state.order,
	// Paginator
	page: (state: ServiceLogStore) => state.page,
	perPage: (state: ServiceLogStore) => state.perPage,
	totalPages: (state: ServiceLogStore) => state.totalPages,
	actions: (state: ServiceLogStore) => state.actions,
};
